import { PagesPermissions } from '@/models/Permissions/enum/PagesPermissions';
import { Tooltip } from '@mui/material';
import { useAppSelector, useUserHasPermissions } from '@store/hooks';
import { isDef } from '@utils/def';
import { useMemo, useState } from 'react';

import { ICONS_TYPES } from '../Icon';

import SubMenu from './components/SubMenu';
import { IconWrapper } from './components/SubMenu/styles';
import { ButtonItems } from './constants';
import { NavItemId } from './enums';
import {
  ButtonContainer,
  Divider,
  DrawerHeader,
  FooterContainer,
  HomeLinkContainer,
  OpenpanelIcon,
  Root,
  StyledDrawer,
} from './styles';
import { Props } from './types';

const LeftNavbar = ({ className, isSidebarOpen, setIsSidebarOpen }: Props) => {
  const { isAuthenticated, permissions } = useAppSelector(
    (state) => state.auth,
  );

  const [showSubNav, setShowSubNav] = useState<string[]>([]);

  const hasMarketDataWidgets = useUserHasPermissions(
    PagesPermissions.marketDataWidgetsPage,
  );

  const filteredNavButtonsByPermissions = useMemo(() => {
    const filtered = ButtonItems.map((item) => {
      if (item.subNav) {
        const filteredSubItems = item.subNav.filter(
          (subItem) =>
            !isDef(subItem.permission) ||
            permissions.includes(subItem.permission),
        );

        return { ...item, subNav: filteredSubItems };
      }

      return item;
    }).filter((item) => {
      if (isDef(item.subNav) && !item.subNav.length) {
        return false;
      }

      // Only show Market Data Curves if user has marketDataCurvesPage but NOT marketDataWidgetsPage
      if (
        item.permission === PagesPermissions.marketDataCurvesPage &&
        hasMarketDataWidgets
      ) {
        return false;
      }

      return !isDef(item.permission) || permissions.includes(item.permission);
    });

    // Remove section labels that have no items underneath them
    return filtered.filter((item, index) => {
      if (item.type !== 'section') return true; // always keep non-section items

      // Check if the next item exists and is not another section label
      const nextItem = filtered[index + 1];
      return isDef(nextItem) && nextItem.type !== 'section';
    });
  }, [permissions, hasMarketDataWidgets]);

  const sideMenuToggleHandler = () => {
    if (isSidebarOpen) {
      setShowSubNav([]);
    }
    setIsSidebarOpen((prev) => !prev);
  };

  const handleSettingsIconClick = () => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true);
      setShowSubNav((prev) =>
        prev.includes(NavItemId.SETTINGS)
          ? prev
          : [...prev, NavItemId.SETTINGS],
      );
    }
  };
  return (
    <Root className={className}>
      <StyledDrawer
        variant="permanent"
        open={isSidebarOpen}
        $isSideBarOpen={isSidebarOpen}>
        <DrawerHeader>
          <HomeLinkContainer>
            <IconWrapper>
              <OpenpanelIcon
                onClick={sideMenuToggleHandler}
                icon={ICONS_TYPES.Sidepanel}
                size={30}
              />
            </IconWrapper>
          </HomeLinkContainer>
        </DrawerHeader>
        <Divider />
        {isAuthenticated && (
          <>
            <ButtonContainer $isOpen={isSidebarOpen}>
              {filteredNavButtonsByPermissions
                .filter((item) => item.title !== NavItemId?.SETTINGS)
                .map((item, index) => (
                  <SubMenu
                    item={item}
                    isOpenedNavBar={isSidebarOpen}
                    key={index}
                    showSubNav={showSubNav}
                    setShowSubNav={setShowSubNav}
                  />
                ))}
            </ButtonContainer>

            <FooterContainer>
              {filteredNavButtonsByPermissions.find(
                (item) => item.title === NavItemId.SETTINGS,
              ) && (
                <SubMenu
                  item={
                    filteredNavButtonsByPermissions.find(
                      (item) => item.title === NavItemId.SETTINGS,
                    )!
                  }
                  isOpenedNavBar={isSidebarOpen}
                  showSubNav={showSubNav}
                  setShowSubNav={setShowSubNav}
                  onCollapsedClick={handleSettingsIconClick}
                />
              )}
            </FooterContainer>
          </>
        )}
      </StyledDrawer>
    </Root>
  );
};

export default LeftNavbar;
